import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { Checkbox } from 'antd';
import InfiniteScroll from 'react-infinite-scroll-component';
import FilterNewIcon from '../assets/svgIcons/FilterNewIcon';
import GlobalButton from './GlobalButton';
import AffinitySearchResultItemSkeleton from '../skeleton/AffinitySearchResultItemSkeleton';
import {
  greyColor, primaryColor, primaryFont, primaryHeadingColor, scrollbar, secondaryFont, wMBold, wMedium,
} from '../../../Utils/utilsStyle';
import AlertsSearch from '../../alerts/components/AlertsSearch';
import { Filter } from '../../../Types/FilterTypes';
import { CompanyDistinctsData } from '../../../Types/ResponseBodies';
import { SubFilterSelection } from '../../../Types/GlobalTypes';

export type SubFilterOption = string | Filter | CompanyDistinctsData;

type NormalizedSubFilterOption = {
  id: string;
  label: string;
  logo?: string;
  color?: string;
};

const StyledWrapper = styled.div`
width: 340px;
  .header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 4px 16px 24px;
    flex-wrap: wrap;
    row-gap: 8px;
    .validation-message {
      width: 100%;
      color: #ff1f2d;
      font-family: ${primaryFont};
      font-size: 16px;
      font-weight: ${wMedium};
      line-height: 20px;
      text-align: center;
    }
    .title {
      display: flex;
      align-items: center;
      column-gap: 6px;
      .icon {
        svg {
          width: 22px;
          height: 20px;
          path {
            fill: #000 !important;
          }
        }
      }
      .text {
        color: ${primaryHeadingColor};
        font-family: ${secondaryFont};
        font-size: 20px;
        font-weight: ${wMBold};
        line-height: normal;
        letter-spacing: -0.609px;
      }
    }
  }
  .content-box {
      background: #F8F8F8;
      padding: 22px 16px;
      display: flex;
      flex-direction: column;
      row-gap: 12px;
      .selected-div {
        display: flex;
        align-items: center;
        justify-content: space-between;
        .text {
          color: #67696A;
          font-family: ${primaryFont};
          font-size: 18px;
          font-weight: ${wMedium};
          line-height: 16px;
          letter-spacing: -0.375px;
        }
        .unselect-btn {
          color: ${primaryColor};
          font-family: ${primaryFont};
          font-size: 14px;
          font-weight: ${wMedium};
          line-height: 16px;
          letter-spacing: -0.4px;
          cursor: pointer;
        }
      }
      .search-box {
        .search-wrapper {
          margin-bottom: 0px;
          .ant-input-affix-wrapper {
            height: 32px;
            .ant-input {
              max-width: 220px;
            }
          }
          .cross-icon {
            top: 6px;
            right: 7px;
          }
        }
      }
      .data {
        width: 100%;
        display: flex;
        flex-direction: column;
        row-gap: 12px;
        height: 300px;
        border-radius: 0px !important;
        ${scrollbar}
        ::-webkit-scrollbar-track {
          background: transparent
        }
        ::-webkit-scrollbar-thumb {
          border-radius: 20px;
        }
        .filter-item {
          display: flex;
          align-items: center;
          column-gap: 6px;
          cursor: pointer;
          .ant-checkbox-wrapper {
            width: auto;
            margin-bottom: 0px;
          }
          .item-logo {
            width: 40px;
            height: 28px;
            object-fit: cover;
            flex-shrink: 0;
            border-radius: 3.429px;
            border: 1px solid #DADADA;
            background: #FFF;
            box-shadow: 0 0 7.213px -0.849px rgba(0, 0, 0, 0.04);
            padding: 3px;
          }
          .item-color {
            width: 16px;
            height: 16px;
            min-width: 16px;
            border-radius: 50%;
            display: inline-block;
            flex-shrink: 0;
          }
          .item-name {
            color: ${greyColor} !important;
            font-family: ${primaryFont};
            font-size: 16px;
            font-weight: ${wMedium};
            line-height: 20px;
            max-width: 230px;
          }
        }
        .infinite-scroll-component__outerdiv {
          width: 100%;
        }
        .infinite-scroll-component {
          display: flex !important;
          flex-direction: column;
          row-gap: 12px;
          overflow: auto !important;
          ${scrollbar}
          overflow-x: hidden !important;
          border-radius: 0px !important;
          ::-webkit-scrollbar-track {
            background: transparent
          }
          ::-webkit-scrollbar-thumb {
            border-radius: 20px;
          }
        }
        .filter-loader {
          display: flex;
          justify-content: center;
          padding: 8px 0;
        }
        .no-results {
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: ${greyColor} !important;
        }
      }
    }
`;

type Props = {
  category: string;
  title: string;
  data: SubFilterOption[];
  hasMore?: boolean;
  onLoadMore?: (searchTerm?: string) => void;
  loading?: boolean;
  onSearch?: (searchTerm: string) => void;
  selection: SubFilterSelection;
  applyDisabled?: boolean;
  onSelectionChange?: (selected: SubFilterSelection) => void;
  onApply?: (category: string, selected: SubFilterSelection) => void;
};

const defaultProps = {
  category: '',
  title: '',
  data: [] as SubFilterOption[],
  hasMore: false,
  loading: false,
  applyDisabled: false,
  onSelectionChange() { },
  onApply() { },
};

const isCompanyOption = (option: SubFilterOption): option is CompanyDistinctsData => {
  if (typeof option !== 'object' || option === null || 'filterName' in option) {
    return false;
  }

  return ('companyId' in option && 'companyName' in option)
    || ('id' in option && 'name' in option);
};

const isFilterOption = (option: SubFilterOption): option is Filter => (
  typeof option === 'object' && option !== null && 'filterName' in option
);

const normalizeOption = (option: SubFilterOption): NormalizedSubFilterOption => {
  if (typeof option === 'string') {
    return { id: option, label: option };
  }

  if (isCompanyOption(option)) {
    const company = option as CompanyDistinctsData & {
      id?: number | string;
      name?: string;
      logo?: string;
    };
    const id = company.companyId ?? company.id;
    const label = company.companyName ?? company.name ?? '';

    return {
      id: id === undefined || id === null ? '' : String(id),
      label,
      logo: company.companyLogo ?? company.logo,
    };
  }

  if (isFilterOption(option)) {
    const color = option.filterColor && option.filterColor !== 'null'
      ? option.filterColor
      : undefined;

    return {
      id: option.filterId || option.filterName,
      label: option.filterName,
      color,
    };
  }

  return { id: '', label: '' };
};

const normalizeSearchQuery = (value: string) => value.trim().toLowerCase();

const matchesExcludedQuery = (label: string, queries: string[] = []) => (
  queries.some((query) => label.toLowerCase().includes(query))
);

export const isSubFilterOptionSelected = (
  optionName: string,
  selection: SubFilterSelection,
) => {
  if (selection.all) {
    if (selection.selected.includes(optionName)) {
      return true;
    }
    if (matchesExcludedQuery(optionName, selection.excludedQueries)) {
      return false;
    }
    return !selection.excluded.includes(optionName);
  }
  return selection.selected.includes(optionName);
};

const PeopleSubFilterComponent = function PeopleSubFilterComponent(props: Props) {
  const {
    category,
    title,
    data,
    hasMore,
    loading,
    onLoadMore,
    onSearch,
    selection,
    applyDisabled,
    onSelectionChange,
    onApply,
  } = props;
  const [searchText, setSearchText] = useState('');
  const submittedQueryRef = useRef('');
  const previousLabelsRef = useRef<string[]>([]);
  const showSearch = category !== 'personality' && category !== 'jobLevel';
  const isPaginated = Boolean(onLoadMore);

  const normalizedOptions = useMemo(() =>
    (data ?? []).map(normalizeOption).filter((option) => option.id && option.label),
    [data],
  );

  const visibleOptions = useMemo(() => {
    const query = searchText.trim().toLowerCase();

    if (!showSearch || isPaginated || !query) {
      return normalizedOptions;
    }

    return normalizedOptions.filter((option) => option.label.toLowerCase().includes(query));
  }, [normalizedOptions, searchText, showSearch, isPaginated]);

  const allOptionLabels = useMemo(
    () => normalizedOptions.map((option) => option.label),
    [normalizedOptions],
  );
  const hasNoSelection =
    !selection.all && selection.selected.length === 0;

  const isOptionSelected = (optionName: string) => (
    isSubFilterOptionSelected(optionName, selection)
  );

  const handleSelection = (optionName: string) => {
    if (selection.all) {
      const currentlySelected = isOptionSelected(optionName);
      const matchesQuery = matchesExcludedQuery(optionName, selection.excludedQueries);

      if (currentlySelected) {
        onSelectionChange?.({
          ...selection,
          excluded: selection.excluded.includes(optionName)
            ? selection.excluded
            : [...selection.excluded, optionName],
          selected: selection.selected.filter((item) => item !== optionName),
        });
        return;
      }

      onSelectionChange?.({
        ...selection,
        excluded: selection.excluded.filter((item) => item !== optionName),
        selected: matchesQuery
          ? [...new Set([...selection.selected, optionName])]
          : selection.selected.filter((item) => item !== optionName),
      });
      return;
    }
    const isSelected = selection.selected.includes(optionName);
    onSelectionChange?.({
      ...selection,
      selected: isSelected ? selection.selected.filter((item) => item !== optionName) : [...selection.selected, optionName],
    });
  };

  const visibleOptionLabels = useMemo(
    () => visibleOptions.map((option) => option.label),
    [visibleOptions],
  );
  const areAllVisibleSelected = visibleOptionLabels.length > 0
    && visibleOptionLabels.every((option) => isOptionSelected(option));

  const hasActiveSearch = searchText.trim().length > 0;

  const areAllSelected = areAllVisibleSelected;

  useEffect(() => {
    const current = visibleOptionLabels;
    const previous = previousLabelsRef.current;
    previousLabelsRef.current = current;

    const submitted = submittedQueryRef.current;
    const isAppend = previous.length > 0
      && current.length > previous.length
      && previous.every((label, index) => current[index] === label);

    if (!isAppend || !selection.all || !submitted) {
      return;
    }
    if (!(selection.excludedQueries ?? []).includes(submitted)) {
      return;
    }

    const added = current.slice(previous.length).filter((label) => (
      !selection.excluded.includes(label) && !selection.selected.includes(label)
    ));
    if (!added.length) {
      return;
    }

    onSelectionChange?.({
      ...selection,
      excluded: [...new Set([...selection.excluded, ...added])],
    });
  }, [visibleOptionLabels, selection, onSelectionChange]);

  const handleToggleAll = () => {
    if (!hasActiveSearch) {
      if (areAllVisibleSelected) {
        onSelectionChange?.({
          all: false,
          selected: [],
          excluded: [],
          excludedQueries: [],
        });
        return;
      }

      onSelectionChange?.({
        all: true,
        selected: [],
        excluded: [],
        excludedQueries: [],
      });
      return;
    }

    const query = normalizeSearchQuery(searchText);
    if (areAllVisibleSelected) {
      if (selection.all) {
        onSelectionChange?.({
          ...selection,
          excluded: [
            ...new Set([
              ...selection.excluded,
              ...visibleOptionLabels,
            ]),
          ],
          excludedQueries: [
            ...new Set([
              ...(selection.excludedQueries ?? []),
              query,
            ]),
          ],
          selected: selection.selected.filter(
            (option) => !visibleOptionLabels.includes(option)
              && !option.toLowerCase().includes(query),
          ),
        });
        return;
      }

      onSelectionChange?.({
        ...selection,
        selected: selection.selected.filter(
          (option) => !visibleOptionLabels.includes(option),
        ),
        excludedQueries: [
          ...new Set([
            ...(selection.excludedQueries ?? []),
            query,
          ]),
        ],
      });
      return;
    }

    const remainingQueries = (selection.excludedQueries ?? []).filter((item) => item !== query);
    if (selection.all) {
      const stillExcludedByQuery = visibleOptionLabels.filter((label) => (
        matchesExcludedQuery(label, remainingQueries)
      ));
      onSelectionChange?.({
        ...selection,
        excluded: selection.excluded.filter(
          (option) => !visibleOptionLabels.includes(option),
        ),
        excludedQueries: remainingQueries,
        selected: [
          ...new Set([
            ...selection.selected.filter((option) => !visibleOptionLabels.includes(option)),
            ...stillExcludedByQuery,
          ]),
        ],
      });
      return;
    }

    onSelectionChange?.({
      ...selection,
      excludedQueries: remainingQueries,
      selected: [
        ...new Set([
          ...selection.selected,
          ...visibleOptionLabels,
        ]),
      ],
    });
  };

  const handleApply = () => {
    onApply?.(category, selection);
  };

  const staticOptions = visibleOptions.map((options) => {
    const isSelected = isOptionSelected(options.label);
    return (
      <div className='filter-item' key={options.label} onClick={() => handleSelection(options.label)}>
        <Checkbox
          checked={isSelected}
        />
        {
          options?.color ? (
            <span className='item-color' style={{ backgroundColor: options.color }} />
          ) : (
            null
          )
        }
        <span className='item-name text-truncate'>{options.label}</span>
      </div>
    )
  });

  return (
    <StyledWrapper className='people-sub-filter'>
      <div className='header'>
        <div className='title'>
          <div className='icon'>
            <FilterNewIcon />
          </div>
          <div className='text'>
            Filter
          </div>
        </div>
        <GlobalButton
          title="Apply"
          outlineColor={primaryColor}
          bgColor={primaryColor}
          textColor="#fff"
          borderRadius="8px"
          innerSpace="7px 15px"
          buttonClassName="apply-button"
          onButtonClick={handleApply}
          disabled={applyDisabled || hasNoSelection}
        />
        {(hasNoSelection && visibleOptions.length !== 0) && (
          <div className="validation-message">
            Atleast one filter should be selected
          </div>
        )}
      </div>
      <div className='content-box'>
        <div className='selected-div'>
          <div className='text'>
            {`Select ${title}:`}
          </div>
          {
            allOptionLabels.length > 0 && (
              <div
                className='unselect-btn'
                role='button'
                tabIndex={0}
                onClick={handleToggleAll}
              >
                {areAllSelected ? 'unselect all' : 'select all'}
              </div>
            )
          }
        </div>
        {
          showSearch && (
            <div className='search-box'>
              <AlertsSearch
                placeholderText={`Search ${title}`}
                margin='0 0 10px'
                query={searchText}
                onSearching={(value?: string) => {
                  const nextQuery = (value ?? searchText).trim();
                  submittedQueryRef.current = normalizeSearchQuery(nextQuery);
                  if (isPaginated) {
                    onSearch?.(nextQuery);
                  }
                }}
                onInputChange={(value?: string) => {
                  setSearchText(value ?? '');
                }}
                enableQueryTrim={false}
              />
            </div>
          )
        }
        <div className='data'>
          {
            onLoadMore ? (
              <InfiniteScroll
                dataLength={visibleOptions.length}
                next={() => onLoadMore(searchText)}
                hasMore={Boolean(hasMore) && !loading}
                height={300}
                loader={
                  loading && visibleOptions.length > 0 ? (
                    <>
                      {[1, 2, 3].map((item) => (
                        <AffinitySearchResultItemSkeleton key={`filter-loader-${item}`} />
                      ))}
                    </>
                  ) : null
                }
              >
                {loading && (
                  <>
                    {[1, 2, 3, 4, 5, 6].map((item) => (
                      <AffinitySearchResultItemSkeleton key={`filter-initial-loader-${item}`} />
                    ))}
                  </>
                )}
                {!loading && visibleOptions.length === 0 && (
                  <div className='no-results'>No Results Found.</div>
                )}
                {visibleOptions.map((options) => {
                  const isSelected = isOptionSelected(options.label);
                  return (
                    <div className='filter-item' key={options.label} onClick={() => handleSelection(options.label)}>
                      <Checkbox
                        checked={isSelected}
                      />
                      {
                        options?.logo && (
                          <img
                            className='item-logo'
                            src={options.logo}
                            alt=''
                          />
                        )
                      }
                      <span className='item-name text-truncate'>
                        {options.label}
                      </span>
                    </div>
                  )
                })}

              </InfiniteScroll>
            ) : (
              staticOptions.length > 0 ? staticOptions : (
                <div className='no-results'>No Results Found.</div>
              )
            )
          }
        </div>
      </div>
    </StyledWrapper>
  );
};
PeopleSubFilterComponent.defaultProps = defaultProps;
export default PeopleSubFilterComponent;
